/**
 * Start a brand-new arche in Fias Forge: create the forge (which creates its
 * draft arche), start it so the workspace is booting by the time the page
 * opens, and navigate to it.
 *
 * Used by the "Start a new Arche" chooser so Forge is a first-class way to
 * begin, not only a destination for arches moved out of the builder.
 *
 * A failed CREATE rejects, so the caller can show the reason where the user
 * typed the name. A failed START does not: the forge exists, and its page
 * shows a Start button and the refusal (plan, monthly hours, running cap), so
 * the user is still taken there with a toast explaining what happened.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { forgeQueryKeys } from '../api/queryKeys';
import { FORGE_ARCHE_ID } from '../constants';

export function useCreateForgeArche(): (name: string) => Promise<void> {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useCallback(
    async (name: string) => {
      const { forge } = await api.createForge({ name });
      void queryClient.invalidateQueries({ queryKey: forgeQueryKeys.lists() });
      try {
        await api.startForge(forge.id);
      } catch (err) {
        toast({
          title: 'Your forge was created but did not start',
          description: err instanceof Error ? err.message : 'You can start it from its page.',
        });
      }
      navigate(`/a/${FORGE_ARCHE_ID}/${forge.id}`);
    },
    [navigate, queryClient, toast],
  );
}
